import { Liga, Carrera, Socio, ResultadoCarrera, ConfiguracionLastres } from '../models/types';

/**
 * Calcula el lastre para cada piloto de una liga según la configuración y resultados.
 * Devuelve un objeto: { [socioId]: { kgTotal, detalle: string[] } }
 */
export function calcularLastresLiga({
  liga,
  carreras,
  socios,
  resultados,
  equiposResultados,
  sanciones = []
}: {
  liga: Liga,
  carreras: Carrera[],
  socios: Socio[],
  resultados: ResultadoCarrera[],
  equiposResultados: { [carreraId: string]: { [equipo: string]: number } }, // puesto por equipo en cada carrera
  sanciones?: Array<{ socioId: string; puntosPenalizacion: number; carreraId: string }> // sanciones a aplicar
}): Record<string, { kgTotal: number, detalle: string[] }> {
  console.log('[DEBUG] Iniciando cálculo de lastres');
  console.log(`[DEBUG] Total de resultados: ${resultados.length}`);
  console.log(`[DEBUG] Total de socios: ${socios.length}`);
  console.log('[DEBUG] Socios:', socios.map(s => s.display_name || `${s.nombre} ${s.apellido}`.trim() || 'Sin nombre'));
  const config = liga.configuracionLastres;
  if (!config) return {};

  // Filtrar solo carreras publicadas y ordenarlas por fecha
  const carrerasOrdenadas = carreras
    .filter(c => c.publicada) // Solo carreras publicadas
    .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

  // Mapear resultados por piloto y por carrera, solo para carreras publicadas
  const resultadosPorPiloto: Record<string, ResultadoCarrera[]> = {};
  const carrerasPublicadasIds = new Set(carrerasOrdenadas.map(c => c.id));
  
  resultados.forEach(r => {
    // Solo incluir resultados de carreras publicadas
    if (carrerasPublicadasIds.has(r.carreraId)) {
      if (!resultadosPorPiloto[r.socioId]) resultadosPorPiloto[r.socioId] = [];
      resultadosPorPiloto[r.socioId].push(r);
    }
  });

  // Obtenemos los puntos ya ajustados con sanciones de la clasificación general
  const puntosTotalesPorPiloto: Record<string, number> = {};
  
  // Primero, calculamos los puntos totales de cada piloto sumando todos sus resultados
  socios.forEach(socio => {
    let puntosTotales = 0;
    let totalPuntosSancion = 0;
    
    // Aplicar sanciones específicas para este piloto
    const sancionesPiloto = sanciones.filter(s => s.socioId === socio.id);
    sancionesPiloto.forEach(sancion => {
      const puntosSancion = Math.abs(sancion.puntosPenalizacion || 0);
      console.log(`[DEBUG] Aplicando sanción de ${puntosSancion} puntos a ${socio.display_name} (${socio.id})`);
      totalPuntosSancion += puntosSancion;
    });
    
    // Depuración para José Luis Núñez
    if (socio.display_name && socio.display_name.includes('Jose Luis') && socio.display_name.includes('Nuñez')) {
      console.log(`[DEBUG] === ANÁLISIS DETALLADO PARA ${socio.display_name} ===`);
      console.log(`[DEBUG] ID del socio: ${socio.id}`);
    }
    
    // Primero sumamos todos los puntos de las carreras (1 por DNF o puntos normales)
    carrerasOrdenadas.forEach(carrera => {
      const resultadosCarrera = resultados.filter(r => r.carreraId === carrera.id && r.socioId === socio.id);
      
      // Depuración detallada
      if (socio.display_name && socio.display_name.includes('Jose Luis') && socio.display_name.includes('Nuñez')) {
        console.log(`[DEBUG] Carrera: ${carrera.nombre} (ID: ${carrera.id})`);
        console.log(`[DEBUG] Resultados encontrados para esta carrera:`, resultadosCarrera);
      }
      
      if (resultadosCarrera.length > 0) {
        // Buscamos el resultado principal (puntos positivos o 0)
        const resultadoPrincipal = resultadosCarrera.find(r => r.puntos !== undefined && r.puntos >= 0);
        // Buscamos sanciones (puntos negativos)
        const sanciones = resultadosCarrera.filter(r => r.puntos !== undefined && r.puntos < 0);
        
        // Puntos de la carrera (1 por DNF o puntos normales)
        const puntosCarrera = resultadoPrincipal?.dnf ? 1 : (resultadoPrincipal?.puntos || 0);
        
        // Sumamos puntos de la carrera
        puntosTotales += puntosCarrera;
        
        // Sumamos sanciones
        const puntosSancion = sanciones.reduce((sum, s) => sum + Math.abs(s.puntos || 0), 0);
        totalPuntosSancion += puntosSancion;
        
        // Depuración para José Luis Núñez
        if (socio.display_name && socio.display_name.includes('Jose Luis') && socio.display_name.includes('Nuñez')) {
          console.log(`[DEBUG] ${socio.display_name} - Carrera ${carrera.nombre}:`);
          console.log(`  - Puntos carrera: ${puntosCarrera} (DNF: ${resultadoPrincipal?.dnf || false})`);
          if (puntosSancion > 0) {
            console.log(`  - Sanciones: ${puntosSancion} puntos`);
          }
        }
      }
    });
    
    // Aplicamos las sanciones al total de puntos
    const puntosAntesSanciones = puntosTotales;
    puntosTotales = Math.max(0, puntosTotales - totalPuntosSancion);
    puntosTotalesPorPiloto[socio.id] = puntosTotales;
    
    // Depuración final
    if (socio.display_name && socio.display_name.includes('Jose Luis') && socio.display_name.includes('Nuñez')) {
      console.log(`[DEBUG] === RESUMEN FINAL ===`);
      console.log(`[DEBUG] Puntos de carreras: ${puntosAntesSanciones}`);
      console.log(`[DEBUG] Puntos de sanciones: ${totalPuntosSancion}`);
      console.log(`[DEBUG] Puntos finales: ${puntosTotales}`);
    }
    
    // Depuración para José Luis Núñez
    if (socio.display_name && socio.display_name.includes('Jose Luis') && socio.display_name.includes('Nuñez')) {
      console.log(`[DEBUG] ${socio.display_name} - Puntos antes de sanciones: ${puntosAntesSanciones}`);
      console.log(`[DEBUG] ${socio.display_name} - Total sanciones: ${totalPuntosSancion}`);
      console.log(`[DEBUG] ${socio.display_name} - Puntos finales: ${puntosTotales}`);
    }
  });
  
  // Calculamos los puntos acumulados por carrera (para el historial)
  const puntosAcumulados: Record<string, number[]> = {};
  carrerasOrdenadas.forEach((carrera, idx) => {
    socios.forEach(socio => {
      const resultadosPiloto = (resultadosPorPiloto[socio.id] || []).filter(r => r.carreraId === carrera.id);
      
      // Si hay DNF, 1 punto. Si no, puntos normales (o 0 si no hay resultado)
      let puntos = 0;
      if (resultadosPiloto.length > 0) {
        puntos = resultadosPiloto[0].dnf ? 1 : (resultadosPiloto[0].puntos || 0);
      }
      
      // Restar sanciones de esta carrera
      const sancionesCarrera = resultados.filter(
        r => r.socioId === socio.id && r.carreraId === carrera.id && r.puntos && r.puntos < 0
      );
      const sanciones = Math.abs(sancionesCarrera.reduce((sum, s) => sum + (s.puntos || 0), 0));
      puntos = Math.max(0, puntos - sanciones);
      
      if (!puntosAcumulados[socio.id]) puntosAcumulados[socio.id] = [];
      puntosAcumulados[socio.id][idx] = puntos + (puntosAcumulados[socio.id][idx - 1] || 0);
    });
  });

  // Calcular lastre por piloto para la próxima carrera
  const lastres: Record<string, { kgTotal: number, detalle: string[] }> = {};
  
  // Primero, calcular estadísticas para todos los pilotos
  const estadisticasPilotos: Record<string, { victorias: number, segundos: number, terceros: number }> = {};
  
  // Recopilar todos los socioIds que aparecen en los resultados
  const todosLosSocioIds = new Set<string>();
  
  // Añadir todos los socios conocidos
  socios.forEach(socio => {
    if (socio.id) todosLosSocioIds.add(socio.id);
  });
  
  // Añadir todos los socioIds que aparecen en los resultados de carreras
  carrerasOrdenadas.forEach(carrera => {
    carrera.resultados?.forEach(resultado => {
      if (resultado.socioId) todosLosSocioIds.add(resultado.socioId);
    });
  });
  
  // Inicializar estadísticas para todos los socioIds encontrados
  todosLosSocioIds.forEach(socioId => {
    estadisticasPilotos[socioId] = { victorias: 0, segundos: 0, terceros: 0 };
  });
  
  // Calcular estadísticas de carreras
  carrerasOrdenadas.forEach(carrera => {
    carrera.resultados?.forEach(resultado => {
      if (resultado.socioId) {
        // Asegurarse de que el socioId existe en estadisticasPilotos
        if (!estadisticasPilotos[resultado.socioId]) {
          estadisticasPilotos[resultado.socioId] = { victorias: 0, segundos: 0, terceros: 0 };
        }
        
        // Actualizar estadísticas
        if (resultado.posicion === 1) estadisticasPilotos[resultado.socioId].victorias++;
        if (resultado.posicion === 2) estadisticasPilotos[resultado.socioId].segundos++;
        if (resultado.posicion === 3) estadisticasPilotos[resultado.socioId].terceros++;
      }
    });
  });

  // Crear array con toda la información necesaria para el ordenamiento
  const ranking = socios.map(socio => {
    const stats = estadisticasPilotos[socio.id] || { victorias: 0, segundos: 0, terceros: 0 };
    return {
      id: socio.id,
      puntos: puntosTotalesPorPiloto[socio.id] || 0,
      victorias: stats.victorias,
      segundos: stats.segundos,
      terceros: stats.terceros
    };
  });
  
  // Ordenar según los mismos criterios que en la clasificación general
  ranking.sort((a, b) => {
    if (b.puntos !== a.puntos) return b.puntos - a.puntos;
    if (b.victorias !== a.victorias) return b.victorias - a.victorias;
    if (b.segundos !== a.segundos) return b.segundos - a.segundos;
    if (b.terceros !== a.terceros) return b.terceros - a.terceros;
    return 0;
  });
  
  // Mapa para rastrear posiciones y manejar empates
  const posiciones: Record<string, number> = {};
  
  // IMPORTANTE: Para el cálculo de lastres, solo consideramos los puntos para agrupar a los pilotos
  // Esto garantiza que pilotos con la misma cantidad de puntos reciban la misma posición y lastre
  const gruposEmpatados: {puntos: number, victorias: number, segundos: number, terceros: number, pilotos: typeof ranking}[] = [];
  
  ranking.forEach(piloto => {
    // Buscamos si ya existe un grupo con la misma cantidad de puntos (ignoramos otras estadísticas)
    const grupoExistente = gruposEmpatados.find(g => g.puntos === piloto.puntos);
    
    if (grupoExistente) {
      grupoExistente.pilotos.push(piloto);
    } else {
      gruposEmpatados.push({
        puntos: piloto.puntos,
        victorias: piloto.victorias,  // Mantenemos estas estadísticas para referencia
        segundos: piloto.segundos,    // pero no las usamos para agrupar
        terceros: piloto.terceros,    // a los pilotos empatados
        pilotos: [piloto]
      });
    }
  });
  
  // Ordenamos los grupos por puntos (de mayor a menor)
  gruposEmpatados.sort((a, b) => b.puntos - a.puntos);
  
  // Ordenar las configuraciones de lastre por posición
  const lastresOrdenados = [...config.lastrePorPosicion].sort((a, b) => a.posicion - b.posicion);
  const ultimaPosicionConLastre = lastresOrdenados.length > 0 
    ? lastresOrdenados[lastresOrdenados.length - 1].posicion 
    : 0;
  
  // Contar el número total de pilotos que recibirán lastre según la configuración
  const totalPilotosConLastre = config.lastrePorPosicion.length;
  console.log(`[DEBUG] Total de posiciones que deben recibir lastre: ${totalPilotosConLastre}`);
  
  // Contador para llevar el registro de cuántas posiciones de lastre han sido asignadas
  let indiceLastreAsignado = 0;
  
  // Contador para el número real de pilotos que recibirán lastre (puede ser mayor que totalPilotosConLastre debido a empates)
  let pilotosRealesConLastre = 0;
  
  // Asignar lastres teniendo en cuenta los empates
  let posicionActual = 1;
  
  // Procesar cada grupo de pilotos empatados
  for (const grupo of gruposEmpatados) {
    // IMPORTANTE: Para los pilotos con la misma cantidad de puntos, buscamos el lastre
    // correspondiente a su posición real en la clasificación (posicionActual)
    
    // IMPORTANTE: Para el cálculo de lastres con empates, usamos un índice secuencial
    // para asignar los lastres en orden, independientemente de la posición real en la clasificación
    let lastreConfig;
    
    // Solo asignamos lastre si aún no hemos asignado todos los lastres configurados
    if (indiceLastreAsignado < totalPilotosConLastre) {
      // Usamos el índice actual para obtener la configuración de lastre correspondiente
      // Esto garantiza que no se salte ningún valor de lastre, incluso cuando hay empates
      lastreConfig = lastresOrdenados[indiceLastreAsignado];
    }
    
    // Determinar el lastre base para esta posición
    // Si no hay configuración para esta posición, el lastre base es 0
    const lastreBase = lastreConfig ? lastreConfig.kg : 0;
    
    // Verificar si esta posición debe recibir lastre según la configuración
    // Consideramos el índice de lastre actual para determinar si aún quedan lastres por asignar
    const debeRecibirLastre = indiceLastreAsignado < totalPilotosConLastre;
    
    // Registrar información de depuración
    console.log(`[DEBUG] Posición ${posicionActual}: ${grupo.pilotos.length} pilotos con ${grupo.puntos} puntos`);
    if (debeRecibirLastre && lastreConfig) {
      console.log(`[DEBUG] Asignando lastre de posición ${posicionActual} (${lastreBase}kg) a ${grupo.pilotos.length} pilotos empatados`);
    } else {
      console.log(`[DEBUG] Sin lastre para la posición ${posicionActual} (fuera del rango configurado o sin configuración)`);
      // Si no debe recibir lastre, forzamos el lastre base a 0
      // Esto asegura que los pilotos fuera del rango configurado no reciban lastre
      // Esto es para los pilotos que están más allá de la posición 10
    }
    
    // IMPORTANTE: Guardar la posición actual para todos los pilotos del grupo
    // para que todos muestren exactamente la misma posición en el detalle
    const posicionGrupo = posicionActual;
    
    // Asignar el mismo lastre a todos los pilotos del grupo
    for (const piloto of grupo.pilotos) {
      // Todos los pilotos del mismo grupo tienen la misma posición
      const detalle: string[] = [
        `Posición en el campeonato: ${posicionGrupo}º con ${grupo.puntos} puntos`
      ];
      
      // Determinamos si este piloto debe recibir lastre según su posición
      // Solo reciben lastre los pilotos dentro del rango configurado (hasta la posición 10)
      let kgLastre = 0;
      
      if (debeRecibirLastre && lastreConfig) {
        // Si debe recibir lastre y hay una configuración para su posición
        kgLastre = lastreBase;
        detalle.push(`Lastre base: ${lastreBase} kg`);
      } else {
        // Si no debe recibir lastre o no hay configuración para su posición
        detalle.push('Sin lastre asignado (posición fuera del rango configurado)');
      }
      
      // Aplicar reducción por equipo si corresponde
      const socio = socios.find(s => s.id === piloto.id);
      if (socio?.EQUIPO && carrerasOrdenadas.length > 0) {
        // Calcular la posición del equipo basada en la clasificación general
        const clasificacionEquipos: {[key: string]: number} = {};
        
        // Inicializar puntos por equipo
        socios.forEach(s => {
          if (s.EQUIPO) {
            clasificacionEquipos[s.EQUIPO] = 0;
          }
        });
        
        // Sumar puntos de todos los pilotos del equipo
        Object.entries(puntosTotalesPorPiloto).forEach(([socioId, puntos]) => {
          const s = socios.find(s => s.id === socioId);
          if (s?.EQUIPO) {
            clasificacionEquipos[s.EQUIPO] += puntos;
          }
        });
        
        // Ordenar equipos por puntos (de mayor a menor)
        const equiposOrdenados = Object.entries(clasificacionEquipos)
          .sort((a, b) => b[1] - a[1])
          .map(([nombre], index) => ({
            nombre,
            posicion: index + 1
          }));
        
        // Encontrar la posición del equipo del piloto actual
        const posicionEquipo = equiposOrdenados.find(e => e.nombre === socio.EQUIPO)?.posicion;
        
        if (posicionEquipo) {
          const reduccion = config.reduccionPorEquipo?.find(r => r.puesto === posicionEquipo)?.kg || 0;
          if (reduccion !== 0) {  // Solo aplicar si hay reducción
            const lastreAnterior = kgLastre;
            kgLastre = Math.max(0, kgLastre + reduccion);
            detalle.push(`Reducción por equipo (posición ${posicionEquipo}º): ${reduccion} kg (${lastreAnterior} kg → ${kgLastre} kg)`);
          }
        }
      }
      
      // Asegurar que el lastre no sea negativo
      kgLastre = Math.max(0, kgLastre);
      
      // Guardar el lastre para este piloto
      lastres[piloto.id] = {
        kgTotal: kgLastre,
        detalle
      };
    }
    
    // Si este grupo recibe lastre, actualizamos los contadores
    if (debeRecibirLastre && lastreConfig) {
      // Incrementamos el índice de lastre para el próximo grupo
      indiceLastreAsignado += 1;
      // Contamos el número real de pilotos que reciben lastre (incluidos los empatados)
      pilotosRealesConLastre += grupo.pilotos.length;
      console.log(`[DEBUG] Índice de lastre asignado: ${indiceLastreAsignado} de ${totalPilotosConLastre}`);
      console.log(`[DEBUG] Pilotos reales con lastre: ${pilotosRealesConLastre} (puede ser mayor que ${totalPilotosConLastre} debido a empates)`);
    }
    
    // Avanzar a la siguiente posición, considerando el número de pilotos empatados
    // Si hay pilotos empatados, todos ocupan la misma posición, por lo que la siguiente posición
    // debe ser la actual más el número de pilotos en el grupo
    posicionActual += grupo.pilotos.length;
    
    // Registrar información de depuración sobre la próxima posición
    console.log(`[DEBUG] Próxima posición a procesar: ${posicionActual}`);
  }

  return lastres;
}
